import { useMutation, useQuery } from '@tanstack/react-query'
import { DELETE, GET, PATCH, POST, PUT } from './axios'

/**
 * Hook for fetching data with a GET request.
 * @param {Array<string|number>} queryKey - Unique key for caching (e.g., ['users', 'list'] or ['users', 'detail', 1]).
 * @param {string} url - API endpoint URL (e.g., '/users' or '/users/1').
 * @param {Object} [options] - Optional configuration.
 * @param {Object} [options.params] - Query parameters for the URL (e.g., { limit: 10, sort: 'asc' }).
 * @param {Object} [options.headers] - Custom headers for the request (e.g., { 'X-Custom': 'value' }).
 * @param {import('@tanstack/react-query').UseQueryOptions} [options.config] - TanStack Query configuration (e.g., { staleTime: 60000, enabled: false }).
 * @example
 * useGetQuery(['users', 'list'], '/users', { params: { limit: 10 } });
 * useGetQuery(['users', 'detail', 1], '/users/1', {
 *   config: { staleTime: 60000, retry: 2 }
 * });
 */
export function useGetQuery (queryKey, url, { params = {}, config = {}, headers = {}, returnFullResponse = false } = {}) {
  return useQuery({
    queryKey,
    queryFn: () => GET(url, { params, headers }),
    select: returnFullResponse ? undefined : (response) => response.data,
    ...config
  })
}

/**
 * Hook for creating data with a POST request.
 * @param {Object} [options] - Optional configuration.
 * @param {import('@tanstack/react-query').UseMutationOptions['onSuccess']} [options.onSuccess] - Callback on successful mutation.
 * @param {import('@tanstack/react-query').UseMutationOptions['onError']} [options.onError] - Callback on mutation error.
 * @param {import('@tanstack/react-query').UseMutationOptions} [options.config] - TanStack Query mutation configuration.
 * @param {Object} [options.headers] - Custom headers for the request (e.g., { 'Content-Type': 'multipart/form-data' }).
 * @returns {Object} - Mutation object with `mutate` function accepting `{ url, payload }`.
 * @example
 * const { mutate } = usePostMutation({
 *   onSuccess: (data) => console.log('Created:', data),
 * });
 * mutate({ url: '/users', payload: { name: 'John' } });
 */
export function usePostMutation ({ onSuccess, onError, config = {}, headers = {} } = {}) {
  return useMutation({
    mutationFn: ({ url, payload }) => POST(url, { payload, headers }),
    onSuccess,
    onError,
    ...config
  })
}

/**
 * Hook for updating data with a PUT request.
 * @param {Object} [options] - Optional configuration.
 * @param {import('@tanstack/react-query').UseMutationOptions['onSuccess']} [options.onSuccess] - Callback on successful mutation.
 * @param {import('@tanstack/react-query').UseMutationOptions['onError']} [options.onError] - Callback on mutation error.
 * @param {import('@tanstack/react-query').UseMutationOptions} [options.config] - TanStack Query mutation configuration.
 * @param {Object} [options.headers] - Custom headers for the request.
 * @returns {Object} - Mutation object with `mutate` function accepting `{ url, payload }`.
 * @example
 * const { mutate } = usePutMutation({
 *   onSuccess: () => console.log('Updated'),
 * });
 * mutate({ url: '/users/1', payload: { name: 'Jane' } });
 */
export function usePutMutation ({ onSuccess, onError, config = {}, headers = {} } = {}) {
  return useMutation({
    mutationFn: ({ url, payload }) => PUT(url, { payload, headers }),
    onSuccess,
    onError,
    ...config
  })
}

/**
 * Hook for partially updating data with a PATCH request.
 * @param {Object} [options] - Optional configuration.
 * @param {import('@tanstack/react-query').UseMutationOptions['onSuccess']} [options.onSuccess] - Callback on successful mutation.
 * @param {import('@tanstack/react-query').UseMutationOptions['onError']} [options.onError] - Callback on mutation error.
 * @param {import('@tanstack/react-query').UseMutationOptions} [options.config] - TanStack Query mutation configuration.
 * @param {Object} [options.headers] - Custom headers for the request.
 * @returns {Object} - Mutation object with `mutate` function accepting `{ url, payload }`.
 * @example
 * const { mutate } = usePatchMutation();
 * mutate({ url: '/users/1', payload: { email: 'new@example.com' } });
 */
export function usePatchMutation ({ onSuccess, onError, config = {}, headers = {} } = {}) {
  return useMutation({
    mutationFn: ({ url, payload }) => PATCH(url, { payload, headers }),
    onSuccess,
    onError,
    ...config
  })
}

/**
 * Hook for deleting data with a DELETE request.
 * @param {Object} [options] - Optional configuration.
 * @param {import('@tanstack/react-query').UseMutationOptions['onSuccess']} [options.onSuccess] - Callback on successful mutation.
 * @param {import('@tanstack/react-query').UseMutationOptions['onError']} [options.onError] - Callback on mutation error.
 * @param {import('@tanstack/react-query').UseMutationOptions} [options.config] - TanStack Query mutation configuration.
 * @param {Object} [options.headers] - Custom headers for the request.
 * @returns {Object} - Mutation object with `mutate` function accepting `{ url, payload }`.
 * @example
 * const { mutate } = useDeleteMutation({
 *   onSuccess: () => console.log('Deleted'),
 * });
 * mutate({ url: '/users/1' });
 */
export function useDeleteMutation ({ onSuccess, onError, config = {}, headers = {} } = {}) {
  return useMutation({
    mutationFn: ({ url, payload }) => DELETE(url, { payload, headers }),
    onSuccess,
    onError,
    ...config
  })
}
